export type ViewerAssetQuality = "standard" | "low";
export type ViewerDeviceClass = "desktop" | "tablet" | "mobile";

export interface ViewerCapabilities {
  viewportWidth: number;
  viewportHeight: number;
  maxTouchPoints: number;
  hardwareConcurrency?: number;
  deviceMemoryGiB?: number;
  prefersReducedMotion: boolean;
}

export interface ViewerProfile {
  deviceClass: ViewerDeviceClass;
  assetQuality: ViewerAssetQuality;
  label: string;
  sphericalHarmonicsDegree: 0 | 1 | 2;
  antialiased: boolean;
  ignoreDevicePixelRatio: boolean;
  inMemoryCompressionLevel: 0 | 1 | 2;
  progressiveLoad: boolean;
  splatAlphaRemovalThreshold: number;
}

type NavigatorWithDeviceHints = Navigator & {
  deviceMemory?: number;
};

export function viewerCapabilitiesFromBrowser(): ViewerCapabilities {
  const navigatorWithHints = navigator as NavigatorWithDeviceHints;
  return {
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
    maxTouchPoints: navigator.maxTouchPoints,
    hardwareConcurrency: navigator.hardwareConcurrency,
    deviceMemoryGiB: navigatorWithHints.deviceMemory,
    prefersReducedMotion: window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  };
}

export function selectViewerProfile(
  capabilities: ViewerCapabilities,
  forceLowQuality = false,
): ViewerProfile {
  const smallestSide = Math.min(capabilities.viewportWidth, capabilities.viewportHeight);
  const touchFirst = capabilities.maxTouchPoints > 0;
  const constrainedMemory = (capabilities.deviceMemoryGiB ?? Infinity) <= 4;
  const constrainedCpu = (capabilities.hardwareConcurrency ?? Infinity) <= 4;
  const mobile = touchFirst && smallestSide <= 600;
  const tablet = touchFirst && !mobile;

  if (forceLowQuality || mobile || constrainedMemory || constrainedCpu) {
    return {
      deviceClass: mobile ? "mobile" : tablet ? "tablet" : "desktop",
      assetQuality: "low",
      label: "軽量",
      sphericalHarmonicsDegree: 0,
      antialiased: false,
      ignoreDevicePixelRatio: true,
      inMemoryCompressionLevel: 2,
      progressiveLoad: !mobile,
      splatAlphaRemovalThreshold: 8,
    };
  }

  if (tablet) {
    return {
      deviceClass: "tablet",
      assetQuality: "standard",
      label: "標準",
      sphericalHarmonicsDegree: 1,
      antialiased: false,
      ignoreDevicePixelRatio: true,
      inMemoryCompressionLevel: 1,
      progressiveLoad: true,
      splatAlphaRemovalThreshold: 5,
    };
  }

  return {
    deviceClass: "desktop",
    assetQuality: "standard",
    label: "高品質",
    sphericalHarmonicsDegree: 2,
    antialiased: true,
    ignoreDevicePixelRatio: false,
    inMemoryCompressionLevel: 1,
    progressiveLoad: true,
    splatAlphaRemovalThreshold: 5,
  };
}
